import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setImageViewer } from '../Redux/store';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import DotIndicator from './DotIndicator';
import ImageDisplay from './ImageDisplay';

/** Full-screen image detail viewer — reads state from Redux, keyboard nav + swipe */
export default function ImageViewerModal() {
  const dispatch = useDispatch();
  const viewer = useSelector((s) => s.ui.imageViewer);

  // No viewer open
  if (!viewer) return null;

  return <ImageViewerContent
    images={viewer.images}
    initialIndex={viewer.initialIndex}
    onClose={() => dispatch(setImageViewer(null))}
  />;
}

/** Inner component so it can use hooks that depend on images/initialIndex */
function ImageViewerContent({ images, initialIndex = 0, onClose }) {
  const [i, setI] = useState(initialIndex);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);

  const next = () => setI((p) => Math.min(p + 1, images.length - 1));
  const prev = () => setI((p) => Math.max(p - 1, 0));

  // Keyboard: Escape closes, arrows navigate
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Swipe gesture handlers
  const onPointerDown = (e) => { setIsDragging(true); dragStartX.current = e.clientX; };
  const onPointerMove = (e) => { if (isDragging) e.preventDefault(); };
  const onPointerUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    const diff = dragStartX.current - e.clientX;
    if (diff > 50) next();
    if (diff < -50) prev();
  };

  const current = images[i];
  const caption = current?.caption || null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-background select-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{ touchAction: 'pan-y' }}
    >
      {/* Image — object-contain, leave space for bottom nav (~60px + safe area) */}
      <ImageDisplay
        key={i}
        imageData={current}
        imageStrategy="contain"
        className="fixed inset-0 bottom-[60px] z-0 h-full w-full"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        showIcons={true}
        iconStyle="top-left"
      />

      {/* Close button */}
      <button
        onClick={onClose}
        className="fixed right-4 top-4 z-[9999] flex h-10 w-10 items-center justify-center rounded-full bg-white/80 text-foreground transition hover:bg-white"
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            disabled={i === 0}
            className="fixed left-4 top-1/2 -translate-y-1/2 z-[9999] flex h-12 w-12 items-center justify-center rounded-full bg-white/80 text-foreground transition hover:bg-white disabled:opacity-0"
            aria-label="Previous"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          <button
            onClick={next}
            disabled={i === images.length - 1}
            className="fixed right-4 top-1/2 -translate-y-1/2 z-[9999] flex h-12 w-12 items-center justify-center rounded-full bg-white/80 text-foreground transition hover:bg-white disabled:opacity-0"
            aria-label="Next"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </>
      )}

      {/* Caption — above dot indicators */}
      {caption && (
        <div className="fixed bottom-[88px] left-4 right-4 z-[9999] text-center" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <span className="inline-block rounded-full bg-black/50 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
            {caption}
          </span>
        </div>
      )}

      {/* Line indicators — above bottom nav */}
      <div className="fixed bottom-[68px] left-4 right-4 z-[9999] flex justify-center" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <DotIndicator total={images.length} index={i} onSelect={setI} />
      </div>
    </div>
  );
}
